import { useState, useCallback } from 'react'
import fetchAPI from '../../../../../lib/utils/fetch-api'

export interface FieldGroup {
  id: number
  name: string
  card_type: string
  sort_order: number
  is_active: boolean
  created_by?: number
  updated_by?: number
  created_at: string
  updated_at: string
  deleted_at?: string
}

export interface FieldGroupField {
  id: number
  group_id: number
  field_name: string
  card_type: string
  sort_order: number
  created_by?: number
  updated_by?: number
  created_at: string
  updated_at: string
  deleted_at?: string
}

export interface FieldGroupWithFields extends FieldGroup {
  fields?: FieldGroupField[]
}

export interface CreateFieldGroupRequest {
  name: string
  card_type: string
  sort_order?: number
  is_active?: boolean
}

export interface UpdateFieldGroupRequest {
  name?: string
  card_type?: string
  sort_order?: number
  is_active?: boolean
}

export interface FieldGroupsListParams {
  card_type?: string
  is_active?: boolean
  page?: number
  limit?: number
}

export interface AssignFieldsRequest {
  fields: Array<{
    field_name: string
    sort_order?: number
  }>
}

export const useFieldGroups = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const handleRequest = useCallback(async <T>(request: Promise<T>): Promise<T | null> => {
    try {
      setLoading(true)
      setError(null)
      const result = await request
      return result
    } catch (err: any) {
      setError(err.message || 'Произошла ошибка')
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const getFieldGroups = useCallback(async (params?: FieldGroupsListParams) => {
    const queryParams = new URLSearchParams()
    if (params?.card_type) queryParams.append('card_type', params.card_type)
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString())
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())

    return handleRequest(
      fetchAPI(`/api/field-groups?${queryParams.toString()}`, {
        method: 'GET'
      })
    )
  }, [handleRequest])

  const getFieldGroup = useCallback(async (id: number) => {
    return handleRequest(
      fetchAPI(`/api/field-groups/${id}`, {
        method: 'GET'
      })
    )
  }, [handleRequest])

  const getFieldGroupWithFields = useCallback(async (id: number) => {
    return handleRequest(
      fetchAPI(`/api/field-groups/${id}/with-fields`, {
        method: 'GET'
      })
    )
  }, [handleRequest])

  const createFieldGroup = useCallback(async (data: CreateFieldGroupRequest) => {
    return handleRequest(
      fetchAPI('/api/field-groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
    )
  }, [handleRequest])

  const updateFieldGroup = useCallback(async (id: number, data: UpdateFieldGroupRequest) => {
    return handleRequest(
      fetchAPI(`/api/field-groups/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
    )
  }, [handleRequest])

  const deleteFieldGroup = useCallback(async (id: number) => {
    return handleRequest(
      fetchAPI(`/api/field-groups/${id}`, {
        method: 'DELETE'
      })
    )
  }, [handleRequest])

  const assignFieldsToGroup = useCallback(async (groupId: number, data: AssignFieldsRequest) => {
    return handleRequest(
      fetchAPI(`/api/field-groups/${groupId}/fields`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
    )
  }, [handleRequest])

  const removeFieldFromGroup = useCallback(async (groupId: number, fieldId: number) => {
    return handleRequest(
      fetchAPI(`/api/field-groups/${groupId}/fields/${fieldId}`, {
        method: 'DELETE'
      })
    )
  }, [handleRequest])

  const getGroupedFieldsByCardType = useCallback(async (cardType: string) => {
    return handleRequest(
      fetchAPI(`/api/field-groups/by-card-type/${cardType}`, {
        method: 'GET'
      })
    )
  }, [handleRequest])

  const updateSortOrder = useCallback(async (updates: Array<{ id: number; sort_order: number }>) => {
    return handleRequest(
      fetchAPI('/api/field-groups/sort-order', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      })
    )
  }, [handleRequest])

  const getCardFields = useCallback(async () => {
    const result = await handleRequest(
      fetchAPI('/api/fields/metadata', {
        method: 'GET'
      })
    )
    
    if (result && result.cardFields) {
      return result.cardFields
    }
    
    return null
  }, [handleRequest])

  const getFieldsUsedByOtherGroups = useCallback(async (cardType: string, excludeGroupId?: number) => {
    const queryParams = new URLSearchParams()
    queryParams.append('card_type', cardType)
    if (excludeGroupId) {
      queryParams.append('exclude_group_id', excludeGroupId.toString())
    }

    return handleRequest(
      fetchAPI(`/api/field-groups/used-fields?${queryParams.toString()}`, {
        method: 'GET'
      })
    )
  }, [handleRequest])

  return {
    loading,
    error,
    clearError,
    getFieldGroups,
    getFieldGroup,
    getFieldGroupWithFields,
    createFieldGroup,
    updateFieldGroup,
    deleteFieldGroup,
    assignFieldsToGroup,
    removeFieldFromGroup,
    getGroupedFieldsByCardType,
    updateSortOrder,
    getCardFields,
    getFieldsUsedByOtherGroups
  }
}
